import { Dispatch, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Pool } from '../../../../../../types';
import { Employer } from '../../../../Employer/EmployerCostSettings/types';
import { ClusterData } from '../../../Clusters/types';

import {
  ControlledAutoComplete,
  ControlledDatePicker,
  ControlledTextField
} from 'components/shared/ControlledFields';

import { useGetPoolMaintenanceTableData } from '../../../../Pools/PoolMaintenance/hooks/useGetPoolMaintenanceTableData';
import { useGetClusters, useGetEmployers } from 'queries';
import { PlanUnit } from '../../types';
import { isNil } from 'ramda';
import { Box } from '@mui/material';
import { useSelector } from '../../../../../../../hooks';
import { HierarchyPlanUnit } from '../utils/treeHierarchy';

export interface Filters {
  referenceDate: Date | null;
  search: string | null;
  clusterId: number | null;
  employerId: number | null;
  poolId: number | null;
  empPoolId: number | null;
  brId: number | null;
  noEmployer?: boolean;
  noPool?: boolean;
  noEmployeePool?: boolean;
}

interface FilterForm extends Pick<Filters, 'referenceDate'> {
  search: string | null;
  cluster: ClusterData | null;
  employer: Employer | null;
  employeePool: Pool | null;
  requestPool: Pool | null;
}

interface Props {
  rows?: PlanUnit[] | HierarchyPlanUnit[];
  dependencies?: any[];
  handleFilterChange: Dispatch<any>;
}

interface NoEmployerOption {
  noEmployer: boolean;
  name: string;
}

interface NoPoolOption {
  noPool: boolean;
  name: string;
}

interface NoEmployeePoolOption {
  noEmployeePool: boolean;
  name: string;
}

const defaultValues = {
  referenceDate: null,
  search: null,
  clusterId: null,
  employerId: null,
  poolId: null,
  empPoolId: null,
  brId: null
};

const isNoEmployerOption = (
  employer: Employer | NoEmployerOption | null
): employer is NoEmployerOption => {
  return !!employer && (employer as NoEmployerOption).noEmployer === true;
};

const isNoPoolOption = (
  pool: Pool | NoPoolOption | null
): pool is NoPoolOption => {
  return !!pool && (pool as NoPoolOption).noPool === true;
};

const isNoEmployeeOption = (
  empPool: Pool | NoEmployeePoolOption | null
): empPool is NoEmployeePoolOption => {
  return !!empPool && (empPool as NoEmployeePoolOption).noEmployeePool === true;
};

export const FilterComponent = ({
  handleFilterChange,
  rows,
  dependencies = []
}: Props) => {
  const { t } = useTranslation('common');
  const { control, watch } = useForm<FilterForm>({
    defaultValues: defaultValues
  });

  const { hasClustersOrgGrant, hasPoolsOrgGrant } = useSelector(
    (state) => state.grants
  );
  const { data: pools } = useGetPoolMaintenanceTableData({
    enabled: hasPoolsOrgGrant
  });
  const { data: clusters } = useGetClusters({ enabled: hasClustersOrgGrant });
  const { data: employers } = useGetEmployers();
  const [filters, setFilters] = useState<Filters>(defaultValues);
  let clearedPlanUnitsDetails: { brId: number; name?: string }[] = [];

  const referenceDate = watch('referenceDate');
  const search = watch('search');
  const cluster = watch('cluster');
  const requestPool = watch('requestPool');
  const employeePool = watch('employeePool');
  const employer = watch('employer');

  const updateFilter = (value, propName) => {
    setFilters((prev) => ({ ...prev, [propName]: value }));
  };

  useEffect(() => {
    const updateFilters = {
      referenceDate: dayjs(referenceDate).isValid() ? referenceDate : null,
      search: search ?? null,
      clusterId: cluster?.clusterId ?? null,
      poolId: isNoPoolOption(requestPool) ? null : requestPool?.id ?? null,
      noPool: isNoPoolOption(requestPool),
      noEmployeePool: isNoEmployeeOption(employeePool),
      empPoolId: employeePool?.id ?? null,
      employerId: isNoEmployerOption(employer)
        ? null
        : employer?.employerId ?? null,
      noEmployer: isNoEmployerOption(employer)
    };

    Object.entries(updateFilters).forEach(([key, value]) =>
      updateFilter(value, key)
    );
  }, [
    search,
    cluster,
    requestPool,
    employeePool,
    employer,
    referenceDate,
    ...dependencies
  ]);

  useEffect(() => {
    handleFilterChange(filters);
  }, [filters, ...dependencies]);

  if (!isNil(rows) && !isNil(referenceDate)) {
    rows.forEach((item) => {
      if (!isNil(item.brId)) {
        if (item.valid && new Date(item.valid.to) >= referenceDate) {
          clearedPlanUnitsDetails.push({
            brId: item.brId,
            name: item.name
          });
        }
      }
    });
    clearedPlanUnitsDetails = clearedPlanUnitsDetails
      .filter(({ name }) => name) // filter out nameless plan units
      .sort((a, b) => {
        return (a?.name ?? '').localeCompare(b?.name ?? '');
      });
  }

  const noEmployerOption: NoEmployerOption = {
    noEmployer: true,
    name: t('noEmployer')
  };
  const noPoolOption: NoPoolOption = { noPool: true, name: t('noPool') };
  const noEmployeeOption: NoEmployeePoolOption = {
    noEmployeePool: true,
    name: t('noEmployeePool')
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ m: 1, ml: 0 }}>
        <ControlledDatePicker
          variant="outlined"
          name="referenceDate"
          label={t('referenceDate')}
          control={control}
        />
      </Box>
      <Box sx={{ flexGrow: 1, m: 1 }}>
        <ControlledTextField
          control={control}
          name="search"
          label={t('search')}
        />
      </Box>
      {hasClustersOrgGrant && (
        <Box sx={{ flexGrow: 1, m: 1, maxWidth: '200px' }}>
          <ControlledAutoComplete
            disableSorting
            optionLabelProp="name"
            idProperty="clusterId"
            variant="outlined"
            control={control}
            name="cluster"
            label={t('cluster')}
            options={clusters ?? []}
            fullWidth
          />
        </Box>
      )}
      <Box sx={{ flexGrow: 1, m: 1, maxWidth: '200px' }}>
        <ControlledAutoComplete
          disableSorting
          optionLabelProp="name"
          idProperty="employerId"
          variant="outlined"
          control={control}
          name="employer"
          label={capitalize(t('employer'))}
          options={[noEmployerOption, ...(employers ?? [])]}
          fullWidth
        />
      </Box>
      {hasPoolsOrgGrant && (
        <>
          <Box sx={{ flexGrow: 1, m: 1, maxWidth: '200px' }}>
            <ControlledAutoComplete
              disableSorting
              optionLabelProp="name"
              variant="outlined"
              control={control}
              name="requestPool"
              label={t('requestPool')}
              options={[noPoolOption, ...(pools ?? [])]}
              fullWidth
            />
          </Box>
          <Box sx={{ flexGrow: 1, ml: 1, my: 1, maxWidth: '200px' }}>
            <ControlledAutoComplete
              disableSorting
              optionLabelProp="name"
              variant="outlined"
              control={control}
              name="employeePool"
              label={t('employeePool')}
              options={[noEmployeeOption, ...(pools ?? [])]}
              fullWidth
            />
          </Box>
        </>
      )}
    </Box>
  );
};
